import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  MapPin,
  Home,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Video,
  User,
  CalendarCheck,
  X,
  Star,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TenantViewings() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyIdFromUrl = searchParams.get('property');
  
  const [activeTab, setActiveTab] = useState('browse');
  const [loading, setLoading] = useState(false);
  
  // Available slots state
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(propertyIdFromUrl || '');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  // My bookings state
  const [myBookings, setMyBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  
  // Favourites state
  const [favourites, setFavourites] = useState([]);
  
  // Verification status
  const [isVerified, setIsVerified] = useState(false);
  const [noShowCount, setNoShowCount] = useState(0);
  
  // Check verification status
  useEffect(() => {
    if (user) {
      setIsVerified(user.is_verified || user.verification_status === 'approved');
      setNoShowCount(user.no_show_count || 0);
    }
  }, [user]);
  
  // Fetch properties with available viewings
  const fetchProperties = useCallback(async () => {
    try {
      // Get properties from favourites or search
      const response = await fetch(`${API_URL}/api/properties?status=available&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(Array.isArray(data) ? data : data.properties || []);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  }, [token]);
  
  // Fetch available slots for a property
  const fetchSlots = useCallback(async (propertyId) => {
    if (!propertyId) return;
    
    setSlotsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/slots/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [token]);
  
  // Fetch my bookings
  const fetchMyBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/my-bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyBookings(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  }, [token]);
  
  // Fetch favourites
  const fetchFavourites = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/viewings/favourites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFavourites(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch favourites:', error);
    }
  }, [token]);
  
  // Initial load
  useEffect(() => {
    if (token) {
      fetchProperties();
      fetchMyBookings();
      fetchFavourites();
    }
  }, [token, fetchProperties, fetchMyBookings, fetchFavourites]);
  
  // Fetch slots when property selected
  useEffect(() => {
    if (selectedProperty) {
      fetchSlots(selectedProperty);
    }
  }, [selectedProperty, fetchSlots]);
  
  // Book a slot
  const handleBookSlot = async (slotId, bookingType = 'in_person') => {
    if (!isVerified) {
      toast.error('Please complete your profile verification before booking viewings', {
        action: {
          label: 'Verify Now',
          onClick: () => navigate('/tenant/onboarding')
        }
      });
      return;
    }
    
    if (noShowCount >= 3) {
      toast.error('You have been blocked from booking viewings due to multiple no-shows. Please contact support.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/book`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slot_id: slotId,
          booking_type: bookingType
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Viewing booked successfully! Check your email for confirmation.');
        fetchSlots(selectedProperty);
        fetchMyBookings();
        setActiveTab('bookings');
      } else {
        if (data.code === 'VERIFICATION_REQUIRED') {
          toast.error('Please complete your profile verification first', {
            action: {
              label: 'Verify Now',
              onClick: () => navigate('/tenant/onboarding')
            }
          });
        } else if (data.code === 'NO_SHOW_BLOCKED') {
          toast.error('You have been blocked due to multiple no-shows');
        } else {
          toast.error(data.detail || data.error || 'Failed to book viewing');
        }
      }
    } catch (error) {
      toast.error('Failed to book viewing');
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel a booking
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this viewing?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/viewings/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Booking cancelled');
        fetchMyBookings();
      } else {
        toast.error('Failed to cancel booking');
      }
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };
  
  // Join waitlist
  const handleJoinWaitlist = async (propertyId) => {
    try {
      const response = await fetch(`${API_URL}/api/viewings/waitlist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ property_id: propertyId })
      });
      
      if (response.ok) {
        toast.success('Added to waitlist! We\'ll notify you when slots become available.');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to join waitlist');
      }
    } catch (error) {
      toast.error('Failed to join waitlist');
    }
  };
  
  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const [hours, minutes] = timeStr.split(':');
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const displayHour = h % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    } catch {
      return dateStr;
    }
  };
  
  // Group slots by date
  const groupSlotsByDate = (slots) => {
    const grouped = {};
    slots.forEach(slot => {
      const dateKey = slot.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="tenant-viewings-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Property Viewings</h1>
          <p className="text-slate-600">Browse available viewing slots and manage your bookings</p>
        </div>
        
        {/* Verification Warning */}
        {!isVerified && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Profile Verification Required</p>
                  <p className="text-sm text-amber-700">Complete your verification to book property viewings</p>
                </div>
              </div>
              <Button onClick={() => navigate('/tenant/onboarding')} className="bg-amber-600 hover:bg-amber-700">
                Verify Now
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* No-Show Warning */}
        {noShowCount > 0 && noShowCount < 3 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="flex items-center gap-3 p-4">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-800">No-Show Warning</p>
                <p className="text-sm text-red-700">
                  You have {noShowCount} no-show(s) recorded. After 3 no-shows, you will be blocked from booking viewings.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="browse" className="gap-2">
              <Calendar className="w-4 h-4" />
              Browse Viewings
            </TabsTrigger>
            <TabsTrigger value="bookings" className="gap-2">
              <CalendarCheck className="w-4 h-4" />
              My Bookings
              {myBookings.length > 0 && (
                <Badge variant="secondary" className="ml-1">{myBookings.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="favourites" className="gap-2">
              <Star className="w-4 h-4" />
              Saved Properties
            </TabsTrigger>
          </TabsList>
          
          {/* Browse Viewings Tab */}
          <TabsContent value="browse">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Property Selection */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Select Property</CardTitle>
                  <CardDescription>Choose a property to view available slots</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(property => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.address_line1}, {property.postcode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedProperty && availableSlots.length === 0 && !slotsLoading && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg text-center">
                      <Bell className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-600 mb-3">No viewing slots available</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleJoinWaitlist(selectedProperty)}
                      >
                        Join Waitlist
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Available Slots */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Available Slots</CardTitle>
                      <CardDescription>
                        {availableSlots.length} slot{availableSlots.length !== 1 ? 's' : ''} available
                      </CardDescription>
                    </div>
                    {selectedProperty && (
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => fetchSlots(selectedProperty)}
                        disabled={slotsLoading}
                      >
                        <RefreshCw className={`w-4 h-4 ${slotsLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedProperty ? (
                    <div className="text-center py-12 text-slate-500">
                      <Home className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a property to see available viewing slots</p>
                    </div>
                  ) : slotsLoading ? (
                    <div className="text-center py-12">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No viewing slots available for this property</p>
                      <p className="text-sm mt-2">Join the waitlist to be notified</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {groupSlotsByDate(availableSlots).map(([dateKey, slots]) => (
                        <div key={dateKey}>
                          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {formatDate(dateKey)}
                          </h4>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {slots.map(slot => (
                              <div
                                key={slot.id}
                                className={`
                                  p-4 border rounded-lg transition-colors
                                  ${slot.status === 'available' 
                                    ? 'bg-white hover:border-primary cursor-pointer' 
                                    : 'bg-slate-50 opacity-60'
                                  }
                                `}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-slate-500" />
                                    <span className="font-medium">
                                      {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                    </span>
                                  </div>
                                  <Badge variant={slot.status === 'available' ? 'default' : 'secondary'}>
                                    {slot.status}
                                  </Badge>
                                </div>
                                
                                {slot.slot_type && (
                                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
                                    {slot.slot_type === 'remote' ? (
                                      <><Video className="w-3 h-3" /> Video Call</>
                                    ) : (
                                      <><User className="w-3 h-3" /> In Person</>
                                    )}
                                  </div>
                                )}
                                
                                {slot.status === 'available' && (
                                  <div className="flex gap-2 mt-3">
                                    <Button
                                      size="sm"
                                      className="flex-1"
                                      onClick={() => handleBookSlot(slot.id, 'in_person')}
                                      disabled={loading || !isVerified}
                                    >
                                      <User className="w-3 h-3 mr-1" />
                                      Book In Person
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleBookSlot(slot.id, 'remote')}
                                      disabled={loading || !isVerified}
                                    >
                                      <Video className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* My Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>My Viewing Bookings</CardTitle>
                    <CardDescription>Manage your upcoming and past viewings</CardDescription>
                  </div>
                  <Button variant="outline" size="icon" onClick={fetchMyBookings} disabled={bookingsLoading}>
                    <RefreshCw className={`w-4 h-4 ${bookingsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {bookingsLoading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : myBookings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CalendarCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No viewing bookings yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setActiveTab('browse')}
                    >
                      Browse Available Viewings
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myBookings.map(booking => (
                      <div
                        key={booking.id}
                        className={`
                          p-4 border rounded-lg
                          ${booking.status === 'confirmed' ? 'border-emerald-200 bg-emerald-50' : ''}
                          ${booking.status === 'cancelled' ? 'border-slate-200 bg-slate-50 opacity-60' : ''}
                          ${booking.status === 'completed' ? 'border-blue-200 bg-blue-50' : ''}
                        `}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Home className="w-4 h-4 text-slate-500" />
                              <span className="font-medium">{booking.property_address || 'Property'}</span>
                              <Badge variant={
                                booking.status === 'confirmed' ? 'default' :
                                booking.status === 'completed' ? 'secondary' : 'outline'
                              }>
                                {booking.status}
                              </Badge>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(booking.date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                              </span>
                              <span className="flex items-center gap-1">
                                {booking.booking_type === 'remote' ? (
                                  <><Video className="w-4 h-4" /> Video Call</>
                                ) : (
                                  <><User className="w-4 h-4" /> In Person</>
                                )}
                              </span>
                            </div>
                          </div>
                          
                          {booking.status === 'confirmed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleCancelBooking(booking.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        {booking.status === 'completed' && !booking.feedback_submitted && (
                          <div className="mt-3 pt-3 border-t">
                            <Button size="sm" variant="outline">
                              Leave Feedback
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Saved Properties Tab */}
          <TabsContent value="favourites">
            <Card>
              <CardHeader>
                <CardTitle>Saved Properties</CardTitle>
                <CardDescription>Properties you're interested in - get notified when viewing slots become available</CardDescription>
              </CardHeader>
              <CardContent>
                {favourites.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No saved properties yet</p>
                    <p className="text-sm mt-2">Save properties while browsing to get viewing notifications</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {favourites.map(fav => (
                      <div
                        key={fav.id}
                        className="p-4 border rounded-lg flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium">{fav.property_address}</p>
                          <p className="text-sm text-slate-500">{fav.postcode}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {fav.has_available_slots ? (
                            <Badge className="bg-emerald-500">Slots Available</Badge>
                          ) : (
                            <Badge variant="outline">No Slots</Badge>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedProperty(fav.property_id);
                              setActiveTab('browse');
                            }}
                          >
                            View Slots
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
